import type { LLMProvider, ProviderConfig } from "./types";
import { AnthropicProvider } from "./providers/anthropic";
import { OpenAIProvider } from "./providers/openai";
import { OllamaProvider } from "./providers/ollama";

const DEFAULT_MAX_TOKENS = 1024;

/**
 * Builds a concrete {@link LLMProvider} from a serializable
 * {@link ProviderConfig}. The `custom` kind is delegated to whichever
 * protocol its `protocol` field names, so a self-hosted OpenAI-compatible
 * gateway reuses the OpenAI implementation, etc.
 */
export function createProvider(cfg: ProviderConfig): LLMProvider {
  const maxTokens = cfg.maxTokens ?? DEFAULT_MAX_TOKENS;

  switch (cfg.kind) {
    case "anthropic":
      return new AnthropicProvider({
        baseUrl: cfg.baseUrl ?? "https://api.anthropic.com",
        apiKey: cfg.apiKey,
        model: cfg.model,
        maxTokens,
        anthropicVersion: cfg.anthropicVersion ?? "2023-06-01",
      });

    case "openai":
      return new OpenAIProvider({
        baseUrl: cfg.baseUrl ?? "https://api.openai.com/v1",
        apiKey: cfg.apiKey,
        model: cfg.model,
        maxTokens,
        organization: cfg.organization,
      });

    case "ollama":
      return new OllamaProvider({
        baseUrl: cfg.baseUrl ?? "http://localhost:11434",
        model: cfg.model,
        maxTokens,
      });

    case "custom": {
      const protocol = cfg.protocol ?? "openai";
      if (protocol === "anthropic") {
        return new AnthropicProvider({
          baseUrl: cfg.baseUrl,
          apiKey: cfg.apiKey ?? "",
          model: cfg.model,
          maxTokens,
          anthropicVersion: "2023-06-01",
          extraHeaders: cfg.headers,
        });
      }
      if (protocol === "ollama") {
        return new OllamaProvider({
          baseUrl: cfg.baseUrl,
          model: cfg.model,
          maxTokens,
          extraHeaders: cfg.headers,
        });
      }
      return new OpenAIProvider({
        baseUrl: cfg.baseUrl,
        apiKey: cfg.apiKey,
        model: cfg.model,
        maxTokens,
        authHeader: cfg.authHeader,
        authScheme: cfg.authScheme,
        extraHeaders: cfg.headers,
      });
    }

    default: {
      // Exhaustiveness guard — a new ProviderKind must be handled above.
      const _never: never = cfg;
      throw new Error(`Unknown provider kind: ${JSON.stringify(_never)}`);
    }
  }
}

/** Returns true when the config's backend can honour LLM tool calls. */
export function providerSupportsTools(cfg: ProviderConfig): boolean {
  if (cfg.kind === "ollama") return false;
  if (cfg.kind === "custom") return (cfg.protocol ?? "openai") !== "ollama";
  return true;
}
